export interface SandboxConnectOption {
  /** The value the PATCH carries: flow uuid, app_id, or String(trunk.id). */
  id: string
  label: string
}

export interface SandboxConnectPage<T> {
  items: T[]
  /** True when the source says there is another page after this one. */
  hasNext: boolean
}

export interface PagedOptions {
  options: SandboxConnectOption[]
  /**
   * True when the page cap stopped the walk before the source ran out. Surfaced
   * in the dialog: a picker that silently drops the tail reads as "that
   * application does not exist".
   */
  truncated: boolean
}

/** One request per page; 100 keeps a normal account to a single round trip. */
export const CONNECT_PAGE_SIZE = 100

/**
 * Hard stop on the walk. A runaway `hasNext` (or an account with thousands of
 * applications) must not turn opening a dialog into an unbounded request loop.
 */
export const CONNECT_MAX_PAGES = 10

/**
 * Walks every page of a picker's source.
 *
 * The three sources page differently - agents by `page` against a `total`,
 * applications and trunks by `offset` against a `meta.next` - so each caller
 * supplies its own one-page fetch and this owns only the walking and the cap.
 *
 * Fetching all pages rather than the API's default first page: this is a
 * pick-one list with no search box, so anything not loaded is unselectable, and
 * the default page is 20 (CLAUDE.md failure mode 3, the repo's most repeated
 * bug class).
 */
export const collectPages = async <T>(
  fetchPage: (pageIndex: number) => Promise<SandboxConnectPage<T>>,
  toOption: (item: T) => SandboxConnectOption,
): Promise<PagedOptions> => {
  const options: SandboxConnectOption[] = []

  for (let pageIndex = 0; pageIndex < CONNECT_MAX_PAGES; pageIndex += 1) {
    const page = await fetchPage(pageIndex)
    options.push(...page.items.map(toOption))
    if (!page.hasNext) return { options, truncated: false }
  }

  return { options, truncated: true }
}

/** `meta.next` is a URL when another page exists, null at the end. */
export const hasNextFromMeta = (meta?: { next?: string | null }): boolean => Boolean(meta?.next)

/**
 * The agent options endpoint returns a running `total` rather than a next link,
 * so the walk has to count what it has already seen.
 */
export const hasNextFromTotal = (loaded: number, total?: number): boolean =>
  typeof total === 'number' && loaded < total
